import {
    Container,
    ContainerSize,
    Figure,
    Heading,
    Text,
    Image,
    Wrapper,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
import responsiveImage from "../../../assets/images/responsive-img.png";
import squareImage from "../../../assets/images/square-img.png";

const ImagePage = () => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Images
            </Heading>
            <Text className="mb-4">
                Documentation and examples for responsive images and Bootstrap’s{" "}
                <Text el="code">.img-fluid</Text> and <Text el="code">.img-thumbnail</Text>{" "}
                styles. Behavior matches{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/content/images/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap’s images documentation
                </Wrapper>
                .
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Image component
            </Heading>
            <Text className="mb-4">
                The <Text el="code">Image</Text> component renders a native{" "}
                <Text el="code">&lt;img&gt;</Text> and maps the{" "}
                <Text el="code">fluid</Text> and <Text el="code">thumbnail</Text> props to
                Bootstrap classes. Like pagination items, <Text el="code">img-fluid</Text> and{" "}
                <Text el="code">img-thumbnail</Text> come from props only, not from{" "}
                <Text el="code">className</Text>. Pass other utilities and attributes as usual.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Responsive images
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">fluid</Text> to apply <Text el="code">.img-fluid</Text>, so
                the image scales with its parent and never overflows horizontally.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Wrapper className="border rounded-2 p-3 bg-body-secondary bg-opacity-25">
                        <Image src={responsiveImage} alt="Wide sample photo" fluid />
                    </Wrapper>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {responsiveImageCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Image thumbnails
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">thumbnail</Text> for Bootstrap’s thumbnail border and
                padding treatment.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Image
                        src={squareImage}
                        alt="Square thumbnail sample"
                        thumbnail
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {thumbnailImageCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Aligning images
            </Heading>
            <Text className="mb-4">
                Combine utility classes with <Text el="code">Image</Text> for floats, block
                centering, or alignment inside a text-centered container.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Wrapper className="clearfix mb-3">
                        <Image
                            src={squareImage}
                            alt="Floated start"
                            className="rounded float-start me-3"
                        />
                        <Text>
                            Placeholder text beside a floated image. Use{" "}
                            <Text el="code">float-start</Text> or{" "}
                            <Text el="code">float-end</Text> and spacing utilities as needed.
                        </Text>
                    </Wrapper>
                    <Image
                        src={squareImage}
                        alt="Centered block"
                        className="rounded mx-auto d-block"
                    />
                    <Wrapper className="text-center mt-3">
                        <Image
                            src={squareImage}
                            alt="Centered in text block"
                            className="rounded"
                        />
                    </Wrapper>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {alignImagesCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Picture
            </Heading>
            <Text className="mb-4">
                When using <Text el="code">&lt;picture&gt;</Text>, put Bootstrap image classes
                on the <Text el="code">&lt;img&gt;</Text> (here, the <Text el="code">Image</Text>{" "}
                component), not on the <Text el="code">&lt;picture&gt;</Text> element.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <picture>
                        <source
                            srcSet="https://picsum.photos/id/1062/400/300"
                            type="image/jpeg"
                        />
                        <Image
                            src={squareImage}
                            alt="Picture element example"
                            fluid
                            thumbnail
                        />
                    </picture>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {pictureCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading
                el="h2"
                id="figures"
                className="fw-medium fs-3 mt-5 mb-3 scroll-mt-3"
            >
                Figures
            </Heading>
            <Text className="mb-4">
                Use a <Text el="code">&lt;figure&gt;</Text> for an image with an optional caption.
                Bootstrap’s baseline styles use <Text el="code">.figure</Text>,{" "}
                <Text el="code">.figure-img</Text> on the image, and{" "}
                <Text el="code">.figure-caption</Text> via{" "}
                <Text el="code">Figure.figureCaption</Text> (same pattern as{" "}
                <Text el="code">Pagination.Item</Text>). Pair <Text el="code">fluid</Text> with{" "}
                <Text el="code">className=&quot;figure-img rounded&quot;</Text> so the image stays
                responsive. See{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/content/figures/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap’s figures documentation
                </Wrapper>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Figure>
                        <Image
                            src={responsiveImage}
                            alt="Example wide image in a figure"
                            fluid
                            className="figure-img rounded"
                        />
                        <Figure.figureCaption>
                            A caption for the above image.
                        </Figure.figureCaption>
                    </Figure>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {figureBasicCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Caption alignment
            </Heading>
            <Text className="mb-4">
                Align the caption with text utilities such as{" "}
                <Text el="code">text-end</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Figure>
                        <Image
                            src={responsiveImage}
                            alt="Example wide image with end-aligned caption"
                            fluid
                            className="figure-img rounded"
                        />
                        <Figure.figureCaption className="text-end">
                            A caption for the above image.
                        </Figure.figureCaption>
                    </Figure>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {figureCaptionAlignCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

const responsiveImageCode = `import responsiveImage from "../../../assets/images/responsive-img.png";

<Image src={responsiveImage} alt="Wide sample photo" fluid />`;

const thumbnailImageCode = `import squareImage from "../../../assets/images/square-img.png";

<Image src={squareImage} alt="Square thumbnail sample" thumbnail />`;

const alignImagesCode = `import squareImage from "../../../assets/images/square-img.png";

<Wrapper className="clearfix mb-3">
    <Image
        src={squareImage}
        alt="Floated start"
        className="rounded float-start me-3"
    />
    <Text>…</Text>
</Wrapper>

<Image
    src={squareImage}
    alt="Centered block"
    className="rounded mx-auto d-block"
/>

<Wrapper className="text-center mt-3">
    <Image
        src={squareImage}
        alt="Centered in text block"
        className="rounded"
    />
</Wrapper>`;

const pictureCode = `import squareImage from "../../../assets/images/square-img.png";

<picture>
    <source
        srcSet="https://picsum.photos/id/1062/400/300"
        type="image/jpeg"
    />
    <Image
        src={squareImage}
        alt="Picture element example"
        fluid
        thumbnail
    />
</picture>`;

const figureBasicCode = `import responsiveImage from "../../../assets/images/responsive-img.png";

<Figure>
    <Image
        src={responsiveImage}
        alt="Example wide image in a figure"
        fluid
        className="figure-img rounded"
    />
    <Figure.figureCaption>A caption for the above image.</Figure.figureCaption>
</Figure>`;

const figureCaptionAlignCode = `import responsiveImage from "../../../assets/images/responsive-img.png";

<Figure>
    <Image
        src={responsiveImage}
        alt="Example wide image with end-aligned caption"
        fluid
        className="figure-img rounded"
    />
    <Figure.figureCaption className="text-end">
        A caption for the above image.
    </Figure.figureCaption>
</Figure>`;

export default ImagePage;
